import React, { useState, useRef, useEffect, useCallback } from 'react';
import './AudioRecorder.css';

export const AudioRecorder = ({ isRecording, onStart, onStop, onChunk, disabled, interviewCompleted }) => {
  const [recordingTime, setRecordingTime] = useState(0);
  const [audioLevel, setAudioLevel] = useState(0);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const streamRef = useRef(null);
  const timerRef = useRef(null);
  const analyserRef = useRef(null);

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current) {
      if (mediaRecorderRef.current.state !== 'inactive') {
        try {
          mediaRecorderRef.current.stop();
          console.log('🛑 MediaRecorder stopped');
        } catch (e) {
          console.warn('⚠️ Error stopping MediaRecorder:', e);
        }
      }
      mediaRecorderRef.current = null;
    }

    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => {
        track.stop();
        console.log('🛑 Audio track stopped');
      });
      streamRef.current = null;
    }

    audioChunksRef.current = [];
  }, []);

  const startRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      // Set up audio analysis
      const audioContext = new AudioContext();
      const analyser = audioContext.createAnalyser();
      const microphone = audioContext.createMediaStreamSource(stream);
      microphone.connect(analyser);
      analyserRef.current = analyser;

      // Start level monitoring
      if (analyser && analyser.frequencyBinCount) {
        const dataArray = new Uint8Array(analyser.frequencyBinCount);
        const monitorLevel = () => {
          if (analyserRef.current && analyserRef.current.frequencyBinCount) {
            analyserRef.current.getByteFrequencyData(dataArray);
            const average = dataArray.reduce((a, b) => a + b) / dataArray.length;
            setAudioLevel(average);
            requestAnimationFrame(monitorLevel);
          }
        };
        monitorLevel();
      }

      // Create MediaRecorder
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        // Don't process chunks if interview is completed
        if (interviewCompleted) {
          console.log('⚠️ Interview completed, ignoring audio chunk');
          return;
        }
        
        if (event.data.size > 0) {
          console.log(`🎤 Audio chunk available: ${event.data.size} bytes`);
          // Convert to base64 and send chunk immediately
          const reader = new FileReader();
          reader.onloadend = () => {
            // Double-check interview is still not completed
            if (interviewCompleted) {
              console.log('⚠️ Interview completed during chunk processing, ignoring');
              return;
            }
            
            const base64 = reader.result.split(',')[1];
            audioChunksRef.current.push(base64);
            console.log(`📤 Sending audio chunk via callback, base64 length: ${base64.length}`);
            // Send chunk via callback if provided
            if (onChunk) {
              onChunk(base64);
            } else {
              console.warn('⚠️ onChunk callback not provided');
            }
          };
          reader.onerror = (error) => {
            console.error('❌ Error reading audio chunk:', error);
          };
          reader.readAsDataURL(event.data);
        } else {
          console.warn('⚠️ Audio chunk has size 0');
        }
      };

      mediaRecorder.onstop = () => {
        if (onStop) {
          onStop(audioChunksRef.current);
        }
        // Stop stream
        if (streamRef.current) {
          streamRef.current.getTracks().forEach(track => track.stop());
        }
      };

      mediaRecorder.start(100); // Collect data every 100ms
      
      // Start timer
      setRecordingTime(0);
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);

      if (onStart) {
        onStart();
      }
    } catch (error) {
      console.error('Error starting recording:', error);
      alert('Error accessing microphone. Please check permissions.');
    }
  }, [interviewCompleted, onChunk, onStart, onStop]);

  useEffect(() => {
    if (interviewCompleted) {
      console.log('🛑 Interview completed, forcing stop of recording');
      stopRecording();
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
        streamRef.current = null;
      }
      return;
    }

    if (isRecording) {
      startRecording();
    } else {
      stopRecording();
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [isRecording, interviewCompleted, startRecording, stopRecording]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="audio-recorder">
      <div className="recorder-controls">
        {!isRecording ? (
          <button
            className="btn btn-primary record-btn"
            onClick={onStart}
            disabled={disabled}
          >
            Start Recording
          </button>
        ) : (
          <div className="recording-active">
            <div className="recording-indicator">
              <span className="pulse"></span>
              <span>Recording...</span>
            </div>
            <div className="recording-time">{formatTime(recordingTime)}</div>
            <button
              className="btn btn-danger stop-btn"
              onClick={onStop}
            >
              Stop Recording
            </button>
          </div>
        )}
      </div>

      {isRecording && (
        <div className="audio-level">
          <div 
            className="level-bar"
            style={{ width: `${Math.min((audioLevel || 0) / 2, 100)}%` }}
          ></div>
        </div>
      )}
    </div>
  );
};

